import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { formatDuration } from '@blackblack/shared';
import { useTrack } from '../../lib/entities';
import { navigate } from '../../lib/router';
import { player, usePlayer, useCurrentTrack } from '../../state/player';
import { Icon } from '../../components/ui/Icon';
import { IconButton, cx } from '../../components/ui/primitives';
import { Sheet } from '../../components/ui/Sheet';
import { Cover } from './Cover';
import { useTrackActions } from './useTrackActions';
import styles from './player.module.css';

/** Full-screen Now Playing view with scrubber, transport, shuffle/repeat and queue. */
export function NowPlaying() {
  const current = useCurrentTrack();
  const s = usePlayer((st) => st);
  const actions = useTrackActions();
  const [scrub, setScrub] = useState<number | null>(null);
  const bar = useRef<HTMLDivElement>(null);
  const dragY = useRef<{ y: number; dy: number } | null>(null);
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !player.state.queueOpen) player.setExpanded(false);
      if (e.key === ' ' && !(e.target as HTMLElement).closest('input,button')) {
        e.preventDefault();
        player.toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const trackBase = current;
  const track = useTrack(trackBase ?? { id: '', liked: false });
  if (!current) return null;

  const pos = scrub ?? s.position;
  const fraction = s.duration ? Math.min(1, pos / s.duration) : 0;

  const fromPointer = (e: PointerEvent) => {
    const r = bar.current!.getBoundingClientRect();
    return Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)) * s.duration;
  };

  // Swipe down on the artwork area to minimise.
  const onDown = (e: PointerEvent) => (dragY.current = { y: e.clientY, dy: 0 });
  const onMove = (e: PointerEvent) => {
    if (!dragY.current || !panel.current) return;
    dragY.current.dy = Math.max(0, e.clientY - dragY.current.y);
    panel.current.style.transform = `translateY(${dragY.current.dy}px)`;
  };
  const onUp = () => {
    if (dragY.current && dragY.current.dy > 120) player.setExpanded(false);
    else if (panel.current) panel.current.style.transform = '';
    dragY.current = null;
  };

  return (
    <div className={styles.npRoot} role="dialog" aria-modal="true" aria-label="Nu aan het afspelen">
      <div className={styles.npBackdrop} style={{ backgroundImage: current.coverUrl ? `url("${current.coverUrl}")` : undefined }} />
      <div ref={panel} className={styles.np}>
        <header className={styles.npHeader}>
          <IconButton icon="down" label="Minimaliseren" onClick={() => player.setExpanded(false)} />
          <div className={styles.npContext}>
            <small>Speelt af uit</small>
            <span>{s.context ?? 'BlackBlack'}</span>
          </div>
          <IconButton icon="queue" label="Wachtrij" onClick={() => player.setQueueOpen(true)} />
        </header>

        <div className={styles.npArt} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
          <div className={cx(styles.npArtInner, s.playing && styles.npArtPlaying)}>
            <Cover src={current.coverUrl} size="100%" radius={22} alt={`Artwork van ${current.title}`} />
          </div>
        </div>

        <div className={styles.npInfo}>
          <div className={styles.npTitles}>
            <h2>{current.title}</h2>
            <button
              onClick={() => {
                player.setExpanded(false);
                navigate(`/artist/${current.artist.id}`);
              }}
            >
              {current.artist.name}
            </button>
          </div>
          <IconButton icon="heart" label={track.liked ? 'Niet meer leuk' : 'Vind ik leuk'} active={track.liked} filled={track.liked} size={26} onClick={() => actions.toggleLike(current)} />
        </div>

        {!current.fullPlayback && current.clip && <p className={styles.npPreview}>Preview — volledige versie is (nog) niet beschikbaar door muziekrechten</p>}

        <div
          ref={bar}
          className={styles.scrubber}
          role="slider"
          aria-label="Positie"
          aria-valuemin={0}
          aria-valuemax={Math.round(s.duration)}
          aria-valuenow={Math.round(pos)}
          aria-valuetext={`${formatDuration(pos)} van ${formatDuration(s.duration)}`}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') player.seek(s.position + 5);
            if (e.key === 'ArrowLeft') player.seek(s.position - 5);
          }}
          onPointerDown={(e) => {
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            setScrub(fromPointer(e));
          }}
          onPointerMove={(e) => scrub !== null && setScrub(fromPointer(e))}
          onPointerUp={(e) => {
            player.seek(fromPointer(e));
            setScrub(null);
          }}
        >
          <div className={styles.scrubTrack}>
            <div className={styles.scrubFill} style={{ width: `${fraction * 100}%` }} />
            <div className={styles.scrubThumb} style={{ left: `${fraction * 100}%` }} />
          </div>
        </div>
        <div className={styles.times}>
          <span>{formatDuration(pos)}</span>
          <span>-{formatDuration(Math.max(0, s.duration - pos))}</span>
        </div>

        <div className={styles.transport}>
          <IconButton icon="shuffle" label={s.shuffle ? 'Shuffle uit' : 'Shuffle aan'} active={s.shuffle} onClick={() => player.toggleShuffle()} />
          <IconButton icon="prev" label="Vorige" size={30} onClick={() => player.prev()} />
          <button className={styles.bigPlay} onClick={() => player.toggle()} aria-label={s.playing ? 'Pauze' : 'Afspelen'}>
            <Icon name={s.playing ? 'pause' : 'play'} size={34} />
          </button>
          <IconButton icon="next" label="Volgende" size={30} onClick={() => player.next()} />
          <button className={cx(styles.repeatBtn, s.repeat !== 'off' && styles.repeatOn)} onClick={() => player.cycleRepeat()} aria-label={`Herhalen: ${s.repeat === 'off' ? 'uit' : s.repeat === 'all' ? 'alles' : 'één nummer'}`}>
            <Icon name="repeat" size={22} />
            {s.repeat === 'one' && <span>1</span>}
          </button>
        </div>

        <div className={styles.npFooter}>
          <IconButton icon="playlistAdd" label="Toevoegen aan playlist" onClick={() => actions.addToPlaylist(current)} />
          <div className={styles.volume}>
            <Icon name={s.volume === 0 ? 'mute' : 'volume'} size={18} />
            <input type="range" min={0} max={1} step={0.02} value={s.volume} onChange={(e) => player.setVolume(Number(e.target.value))} aria-label="Volume" />
          </div>
          <IconButton
            icon="grid"
            label="Video's met dit geluid"
            onClick={() => {
              player.setExpanded(false);
              navigate(`/sound/${current.id}`);
            }}
          />
          <IconButton icon="share" label="Delen" onClick={() => actions.share(current)} />
        </div>
        {s.error && <p className={styles.npError}>{s.error}</p>}
      </div>
      {s.queueOpen && <QueueSheet />}
    </div>
  );
}

function QueueSheet() {
  const queue = usePlayer((s) => s.queue);
  const index = usePlayer((s) => s.index);
  const upNext = queue.slice(index + 1);
  return (
    <Sheet open onClose={() => player.setQueueOpen(false)} title="Wachtrij" height="tall">
      <div className={styles.queue}>
        <h3>Nu</h3>
        {queue[index] && <QueueItem i={index} current />}
        <h3>Hierna {upNext.length > 0 && <small>{upNext.length} nummers</small>}</h3>
        {upNext.length === 0 && <p className={styles.queueEmpty}>Niets meer in de wachtrij.</p>}
        {upNext.map((_, k) => (
          <QueueItem key={`${queue[index + 1 + k]!.id}-${k}`} i={index + 1 + k} />
        ))}
      </div>
    </Sheet>
  );
}

function QueueItem({ i, current }: { i: number; current?: boolean }) {
  const t = usePlayer((s) => s.queue[i]);
  if (!t) return null;
  return (
    <div className={cx(styles.queueItem, current && styles.queueCurrent)}>
      <button className={styles.queueMain} onClick={() => player.jumpTo(i)}>
        <Cover src={t.coverUrl} size={44} />
        <span>
          <strong>{t.title}</strong>
          <small>{t.artist.name}</small>
        </span>
      </button>
      {!current && <IconButton icon="close" label="Uit wachtrij verwijderen" size={18} onClick={() => player.removeFromQueue(i)} />}
    </div>
  );
}
